import React, { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Input from "../../pagesSpecific/Login/Input";
import Button from "../../Reusable/Button";
import Checkbox from "../../pagesSpecific/Login/Checkbox";
import ClickableLinkEmbeddedText from "../../Reusable/ClickableLinkEmbeddedText";
import useForm from "../../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../../constants/fieldTypes";
import { useThunk } from "../../../hooks/useThunk";
import { signin } from "../../../store/thunks/authThunks";
import { acceptInvitationExistingUser } from "../../../store/thunks/teamMemberClientThunks";
import showToast from "../../../utils/toastNotifications";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  clearIntendedRoute,
  clearSessionExpired,
  setPendingInvitation,
  clearPendingInvitation,
} from "../../../store/slices/authSlice";

const SignInScreen = ({ isOpen = true, onClose, variant, handlers }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { intendedRoute, isSessionExpired, pendingInvitation } = useSelector(
    (state) => state.auth,
  );

  const [showPassword, setShowPassword] = useState(false);
  const [doSignin, isSigninLoading] = useThunk(signin);
  const [acceptInvitation, isAcceptingInvitation] = useThunk(
    acceptInvitationExistingUser,
  );

  // State for invitation modal
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [localPendingInvitation, setLocalPendingInvitation] = useState(null);

  // Use refs to track state without causing re-renders
  const isProcessingRef = useRef(false);
  const invitationProcessedRef = useRef(false);

  // Get invitation token from URL - only once
  useEffect(() => {
    if (!isOpen || invitationProcessedRef.current) return;

    const params = new URLSearchParams(location.search);
    const invitationToken = params.get("invitation");

    if (invitationToken && !pendingInvitation) {
      // Store the invitation token in Redux
      dispatch(
        setPendingInvitation({
          token: invitationToken,
          source: "url",
          timestamp: Date.now(),
        }),
      );

      invitationProcessedRef.current = true;
    }
  }, [location.search, isOpen, dispatch, pendingInvitation]);

  const prefilledEmail = new URLSearchParams(location.search).get("email");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
    setFormData,
  } = useForm(
    {
      emailOrPhone: "",
      password: "",
      rememberMe: false,
    },
    {
      emailOrPhone: true,
      password: true,
      rememberMe: false,
    },
    {
      emailOrPhone: COMMON_FIELD_TYPES.EMAIL_OR_PHONE,
      password: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      rememberMe: COMMON_FIELD_TYPES.BOOLEAN_FIELD,
    },
  );

  // Prefill email if coming from invitation link - only once
  useEffect(() => {
    if (prefilledEmail && !formData.emailOrPhone) {
      setFormData((prev) => ({
        ...prev,
        emailOrPhone: decodeURIComponent(prefilledEmail),
      }));
    }
  }, [prefilledEmail, setFormData, formData.emailOrPhone]);

  // Handle successful signin
  const handleSignInSuccess = useCallback(
    (result, currentInvitationToken) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      const userData = result?.response?.data?.user;
      const backendPendingInvitation =
        result?.response?.data?.pendingInvitation;

      // Check if user status is pending
      if (userData?.status === "pending") {
        showToast("info", "Your account is awaiting admin approval");
        navigate("/pending-approval");
        onClose?.();
        isProcessingRef.current = false;
        return;
      }

      // Check if there's a pending invitation
      if (backendPendingInvitation) {
        setLocalPendingInvitation(backendPendingInvitation);
        setShowInvitationModal(true);
        isProcessingRef.current = false;
        return;
      }

      // Handle navigation
      if (isSessionExpired) {
        showToast("success", "Sign in Successful");
        dispatch(clearSessionExpired());
        dispatch(clearIntendedRoute());
        onClose?.();
        setTimeout(() => window.location.reload(), 100);
      } else if (intendedRoute) {
        const intendedRoutePath = intendedRoute.split("?")[0];
        if (
          intendedRoutePath.includes("/company-profile") &&
          currentInvitationToken
        ) {
          showToast("success", "Sign in Successful");
          dispatch(clearIntendedRoute());
          onClose?.();
          navigate(`/company-profile?invitation=${currentInvitationToken}`);
        } else {
          showToast("success", "Sign in Successful");
          const routeToNavigate = intendedRoute;
          dispatch(clearIntendedRoute());
          onClose?.();
          navigate(routeToNavigate);
        }
      } else {
        showToast("success", "Sign in Successful");
        onClose?.();
        navigate("/onboarding");
      }

      isProcessingRef.current = false;
    },
    [dispatch, navigate, onClose, intendedRoute, isSessionExpired],
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isProcessingRef.current) return;

    setTouched({ emailOrPhone: true, password: true, rememberMe: true });

    if (!validateForm()) {
      return;
    }

    // Get invitation token from Redux or URL
    const currentInvitationToken =
      pendingInvitation?.token ||
      new URLSearchParams(location.search).get("invitation");

    // Include invitation token in signin request
    const signInData = {
      ...formData,
      invitationToken: currentInvitationToken || undefined,
    };

    try {
      const result = await doSignin(signInData);

      if (result?.success) {
        handleSignInSuccess(result, currentInvitationToken);
      } else {
        showToast("error", result?.error?.message || "Sign in failed");
      }
    } catch (error) {
      console.error("Sign in error:", error);
      showToast("error", "An unexpected error occurred");
    }
  };

  const handleAcceptInvitation = async () => {
    if (!localPendingInvitation || isProcessingRef.current) return;

    isProcessingRef.current = true;
    const { success, error } = await acceptInvitation(
      localPendingInvitation.token,
    );

    if (success) {
      showToast(
        "success",
        `Successfully joined ${localPendingInvitation.companyName} as ${localPendingInvitation.role}!`,
      );
      setShowInvitationModal(false);
      setLocalPendingInvitation(null);
      dispatch(clearPendingInvitation());

      // Navigate based on context
      if (intendedRoute && intendedRoute.includes("/company-profile")) {
        navigate("/company-profile");
      } else if (intendedRoute) {
        const routeToNavigate = intendedRoute;
        dispatch(clearIntendedRoute());
        navigate(routeToNavigate);
      } else {
        navigate("/onboarding");
      }

      onClose?.();
    } else {
      showToast("error", error || "Failed to accept invitation");
    }

    isProcessingRef.current = false;
  };

  const handleDeclineInvitation = () => {
    showToast("info", "Invitation declined");
    setShowInvitationModal(false);
    setLocalPendingInvitation(null);
    dispatch(clearPendingInvitation());

    if (intendedRoute) {
      const routeToNavigate = intendedRoute;
      dispatch(clearIntendedRoute());
      navigate(routeToNavigate);
    } else {
      navigate("/onboarding");
    }
    onClose?.();
  };

  // Handle modal close
  const handleModalClose = useCallback(() => {
    if (pendingInvitation?.source === "url") {
      dispatch(clearPendingInvitation());
    }

    dispatch(clearIntendedRoute());
    dispatch(clearSessionExpired());

    const currentPath = window.location.pathname;
    const protectedRoutes = [
      "/chat",
      "/create-offer",
      "/company-profile",
      "/user-profile",
      "/referral-order",
      "/payment-page",
      "/orders",
      "/my-payments",
      "/services",
      "/subscription-checkout",
      "/my-subscriptions",
      "/manage-subscriptions",
    ];

    const isOnProtectedRoute = protectedRoutes.some((route) =>
      currentPath.startsWith(route),
    );

    if (isOnProtectedRoute) {
      navigate("/onboarding");
    }

    onClose?.();
  }, [dispatch, navigate, onClose, pendingInvitation]);

  const isFormValid =
    formData.emailOrPhone.trim() !== "" &&
    formData.password.trim() !== "" &&
    Object.values(errors).every((err) => err === "");

  // Get invitation token from URL for display
  const displayInvitationToken =
    pendingInvitation?.token ||
    new URLSearchParams(location.search).get("invitation");

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0" onClick={handleModalClose} />

        {/* Modal container */}
        <div
          className="relative w-full max-w-[566px] max-h-[90vh] overflow-y-auto"
          style={{
            opacity: 0.9,
            background: "var(--color-background-01)",
            boxShadow: "var(--form-box-shadow)",
            backdropFilter: "var(--form-backdrop)",
            border: "1px solid var(--color-stroke-01)",
            borderRadius: "16px",
            padding: "clamp(24px, 5vw, 48px)",
            display: "flex",
            flexDirection: "column",
            gap: "clamp(16px, 4vw, 32px)",
          }}
        >
          {/* Close button */}
          <button
            onClick={handleModalClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-[var(--color-content-02)] hover:text-[var(--color-content)] transition-colors"
          >
            <X size={20} />
          </button>

          {/* Title */}
          <div className="text-center">
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-[35px]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                letterSpacing: "2%",
                background: "var(--signin)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              Sign In
            </h1>
            {displayInvitationToken && (
              <div className="mt-2">
                <p className="text-sm text-[var(--color-content-02)] mb-1">
                  Sign in to accept your team invitation
                </p>
                <div className="inline-flex items-center px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Team Invitation Pending
                </div>
              </div>
            )}
            {isSessionExpired && (
              <p className="mt-2 text-sm text-[var(--color-content-02)]">
                Your session has expired. Please sign in again.
              </p>
            )}
          </div>

          {/* Form Container */}
          <div className="w-full max-w-[470px] mx-auto">
            {/* Form */}
            <div className="">
              <Input
                label="Email or Phone Number"
                id="emailOrPhone"
                name="emailOrPhone"
                type="text"
                placeholder="Enter Your Email or Phone Number"
                value={formData.emailOrPhone}
                onChange={(e) =>
                  handleInputChange("emailOrPhone", e.target.value)
                }
                onBlur={() => handleBlur("emailOrPhone", formData.emailOrPhone)}
                onFocus={() => clearError("emailOrPhone")}
                error={touched.emailOrPhone && errors.emailOrPhone}
                isRequired
                inputStyle="bg-transparent text-[var(--color-content-03)] rounded-lg h-12 px-4 py-3"
                labelStyle="font-inter font-medium text-sm text-[var(--color-label)]"
                style={{
                  width: "100%",
                  maxWidth: "470px",
                  height: "41px",
                  transform: "rotate(0deg)",
                  opacity: 1,
                  borderWidth: "0.6px",
                }}
              />

              <Input
                label="Password"
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter Your Password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                onBlur={() => handleBlur("password", formData.password)}
                onFocus={() => clearError("password")}
                error={touched.password && errors.password}
                isRequired
                inputStyle="bg-transparent text-[var(--color-content-03)] rounded-lg h-12 px-4 py-3"
                labelStyle="font-inter font-medium text-sm text-[var(--color-label)]"
                style={{
                  width: "100%",
                  maxWidth: "470px",
                  height: "41px",
                  transform: "rotate(0deg)",
                  opacity: 1,
                  borderWidth: "0.6px",
                }}
                icon={
                  showPassword ? (
                    <AiFillEye
                      className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <AiFillEyeInvisible
                      className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                      onClick={() => setShowPassword(true)}
                    />
                  )
                }
                iconPosition="right"
              />

              <div className="flex flex-col items-start justify-between gap-3 mt-2 sm:flex-row sm:items-center sm:gap-2">
                <Checkbox
                  id="rememberMe"
                  name="rememberMe"
                  label="Remember me"
                  checked={formData.rememberMe}
                  onChange={(name, value) =>
                    handleInputChange("rememberMe", value)
                  }
                  onBlur={() => handleBlur("rememberMe", formData.rememberMe)}
                  onFocus={() => clearError("rememberMe")}
                  isLoginCheckbox
                  checkboxStyle="w-4 h-4 border-[var(--color-stroke-01)]"
                  labelStyle="text-sm text-[var(--color-content-01)] font-inter"
                />

                <button
                  type="button"
                  onClick={handlers?.switchToForgotPassword}
                  className="hover:opacity-80 hover:text-[var(--color-primary)] transition-opacity text-left sm:text-right"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "var(--color-content-01)",
                  }}
                >
                  Forgot Password ?
                </button>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isSigninLoading || !isFormValid}
                variant="login"
                loading={isSigninLoading}
                style={{
                  maxWidth: "470px",
                  width: "100%",
                  height: "51px",
                  transform: "rotate(0deg)",
                  opacity: isSigninLoading || !isFormValid ? 0.5 : 1,
                  borderRadius: "6px",
                  paddingTop: "16px",
                  paddingBottom: "16px",
                  paddingLeft: "5%",
                  paddingRight: "5%",
                  boxSizing: "border-box",
                  cursor:
                    isSigninLoading || !isFormValid ? "not-allowed" : "pointer",
                }}
                className="font-medium mt-16 sm:mt-20 lg:mt-[100px]"
              >
                {isSigninLoading
                  ? "Signing In..."
                  : displayInvitationToken
                    ? "Sign In & Accept Invitation"
                    : "Login"}
              </Button>

              <div className="flex justify-center w-full mt-4">
                <ClickableLinkEmbeddedText
                  fullText="Don't have an Account ? Register"
                  linkedText="Register"
                  onClick={() => {
                    if (pendingInvitation?.source === "url") {
                      dispatch(clearPendingInvitation());
                    }

                    dispatch(clearIntendedRoute());
                    dispatch(clearSessionExpired());
                    onClose?.();

                    const inviteParam = displayInvitationToken
                      ? `?invitation=${displayInvitationToken}`
                      : "";
                    navigate(`/onboarding${inviteParam}`);
                  }}
                  textColor="var(--color-content-01)"
                  linkColor="var(--color-primary)"
                  className="text-center"
                  wrapperClassName=""
                  linkClassName=""
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    display: "inline-flex",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                  linkStyle={{
                    fontWeight: 600,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invitation Acceptance Modal */}
      {showInvitationModal && localPendingInvitation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md"
            style={{
              background: "var(--color-background-01)",
              border: "1px solid var(--color-stroke-01)",
              borderRadius: "16px",
              padding: "32px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-[var(--color-primary)]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3
              className="mb-2 text-xl font-semibold text-center"
              style={{ color: "var(--color-content)" }}
            >
              Team Invitation
            </h3>

            {/* Message */}
            <p
              className="mb-6 text-sm text-center"
              style={{ color: "var(--color-content-02)" }}
            >
              You've been invited to join{" "}
              <span
                className="font-semibold"
                style={{ color: "var(--color-primary)" }}
              >
                {localPendingInvitation.companyName}
              </span>{" "}
              as a{" "}
              <span
                className="font-semibold"
                style={{ color: "var(--color-content)" }}
              >
                {localPendingInvitation.role}
              </span>
              .
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleAcceptInvitation}
                disabled={isAcceptingInvitation}
                loading={isAcceptingInvitation}
                variant="primary"
                className="flex-1 h-11"
                style={{
                  borderRadius: "6px",
                }}
              >
                {isAcceptingInvitation ? "Accepting..." : "Accept Invitation"}
              </Button>
              <Button
                onClick={handleDeclineInvitation}
                disabled={isAcceptingInvitation}
                variant="secondary"
                className="flex-1 h-11 border border-[var(--color-stroke-01)]"
                style={{
                  borderRadius: "6px",
                  background: "transparent",
                  color: "var(--color-content-02)",
                }}
              >
                Decline
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignInScreen;
